import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/ui/Modal';
import ParchmentCard from '@/components/ui/ParchmentCard';
import * as playerService from '@/services/playerService';
import { resolveAssetUrl } from '@/utils/assets';

const filters = [
  { value: 'all', label: 'Todas' },
  { value: 'unlocked', label: 'Minhas' },
  { value: 'locked', label: 'Por ganhar' },
];

const rarityMeta = {
  common: { label: 'Comum', className: 'border-edge bg-surfaceAlt text-fg' },
  rare: { label: 'Rara', className: 'border-ocean-light/40 bg-ocean-light/15 text-ocean-light' },
  epic: { label: 'Épica', className: 'border-gem/40 bg-gem/15 text-gem' },
  legendary: { label: 'Lendária', className: 'border-accent/60 bg-accent/20 text-accent' },
};

const requirementLabels = {
  pois_visited: 'POIs visitados',
  missions_completed: 'Missões completas',
  redemptions: 'Ofertas validadas',
  zone_conquered: 'Zona conquistada',
};

function BadgeIcon({ badge }) {
  if (badge.imageUrl) {
    return (
      <img
        src={resolveAssetUrl(badge.imageUrl)}
        alt=""
        className={`h-full w-full object-cover ${badge.unlocked ? '' : 'grayscale'}`}
      />
    );
  }

  const initials = badge.name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return (
    <span className="font-serif text-xl font-black">
      {initials || 'HC'}
    </span>
  );
}

function BadgeCard({ badge, onPreview }) {
  const rarity = rarityMeta[badge.rarity] || rarityMeta.common;
  const progress = badge.progress || { current: 0, target: 1, percent: 0 };
  const progressPercent = badge.unlocked ? 100 : progress.percent;

  return (
    <ParchmentCard className={`!p-0 text-fg ${badge.unlocked ? '' : 'opacity-88'}`}>
      <div className="flex min-h-[116px] overflow-hidden rounded-[14px]">
        <div className={`flex w-[74px] flex-shrink-0 flex-col items-center justify-center border-r ${
          badge.unlocked ? 'border-accent/40 bg-gradient-to-b from-accent/35 to-accent/10' : 'border-edge/10 bg-gradient-to-b from-surfaceAlt to-surfaceAlt'
        }`}>
          <button
            type="button"
            onClick={() => onPreview(badge)}
            className={`grid h-14 w-14 place-items-center overflow-hidden rounded-full border-[3px] transition active:scale-95 ${
            badge.unlocked ? 'border-accent bg-accent/25 text-accent shadow-gold' : 'border-edge/25 bg-surfaceAlt/10 text-fg/35 shadow-[inset_0_2px_8px_rgba(0,0,0,0.25)]'
          }`}>
            <BadgeIcon badge={badge} />
          </button>
          <span className={`mt-1.5 h-1.5 w-8 rounded-full ${badge.unlocked ? 'bg-forest' : 'bg-surfaceAlt/20'}`} />
        </div>
        <div className="min-w-0 flex-1 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <h2 className="line-clamp-2 flex-1 font-serif text-[15px] font-black leading-[1.05] text-fg">{badge.name}</h2>
            <span className={`mt-0.5 flex-shrink-0 rounded-full border px-2 py-0.5 text-[8px] font-black uppercase ${rarity.className}`}>
              {rarity.label}
            </span>
          </div>
          <p className="mt-1 line-clamp-2 text-[11px] font-extrabold leading-snug text-fg/60">{badge.description}</p>

      <div className="mt-2">
        <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-wide text-fg/55">
          <span>{requirementLabels[progress.type] || 'Progresso'}</span>
          <span>{badge.unlocked ? 'Concluída' : `${progress.current}/${progress.target}`}</span>
        </div>
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surfaceAlt/12">
          <div
            className={`h-full rounded-full ${badge.unlocked ? 'bg-forest' : 'bg-accent'}`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between text-[11px] font-black">
        <span className="rounded-full bg-accent/18 px-2 py-0.5 text-accent">+{badge.coinReward || 0} moedas</span>
        <span className="rounded-full bg-ocean/12 px-2 py-0.5 text-ocean-light">+{badge.xpReward || 0} XP</span>
      </div>

      {badge.unlocked && badge.unlockedAt && (
        <p className="mt-1 text-[10px] font-bold text-forest-light">
          Desbloqueada em {new Date(badge.unlockedAt).toLocaleDateString('pt-PT')}
        </p>
      )}
        </div>
      </div>
    </ParchmentCard>
  );
}

function BadgePreviewModal({ badge, onClose }) {
  if (!badge) return null;

  const rarity = rarityMeta[badge.rarity] || rarityMeta.common;

  return (
    <Modal isOpen={Boolean(badge)} onClose={onClose} className="max-w-[320px]">
      <ParchmentCard className="relative !p-5 text-center text-fg">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full border border-edge/15 bg-surfaceAlt/8 text-sm font-black text-fg/60 active:scale-95"
          aria-label="Fechar medalha"
        >
          X
        </button>

        <div className="mx-auto mt-2 grid h-36 w-36 place-items-center overflow-hidden rounded-full border-4 border-accent bg-accent/20 text-accent shadow-gold">
          <BadgeIcon badge={badge} />
        </div>

        <span className={`mx-auto mt-4 inline-flex rounded-full border px-3 py-1 text-[10px] font-black uppercase ${rarity.className}`}>
          {rarity.label}
        </span>
        <h2 className="mx-auto mt-3 max-w-[240px] font-serif text-xl font-black leading-tight text-fg">
          {badge.name}
        </h2>
        <p className="mx-auto mt-2 max-w-[240px] text-xs font-extrabold leading-snug text-fg/65">
          {badge.description}
        </p>
      </ParchmentCard>
    </Modal>
  );
}

export default function BadgesPage() {
  const [data, setData] = useState({ badges: [], unlocked: 0, total: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBadge, setSelectedBadge] = useState(null);

  const completion = data.total ? Math.round((data.unlocked / data.total) * 100) : 0;

  const visibleBadges = useMemo(() => {
    if (filter === 'unlocked') return data.badges.filter((badge) => badge.unlocked);
    if (filter === 'locked') return data.badges.filter((badge) => !badge.unlocked);
    return data.badges;
  }, [data.badges, filter]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    playerService.getBadges()
      .then((res) => {
        if (!active) return;
        const payload = res.data.data;
        setData(Array.isArray(payload) ? { badges: payload.map((item) => item.badge || item), unlocked: payload.length, total: payload.length } : payload);
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.message || 'Não foi possível carregar medalhas.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <main className="h-full overflow-y-auto bg-background px-4 pb-4 pt-3">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.18em] text-muted">Conquistas</p>
        <h1 className="font-serif text-xl font-black text-accent">Medalhas</h1>
      </div>

      <ParchmentCard className="mt-3 !p-3 text-fg">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-fg/55">Coleção</p>
            <strong className="font-serif text-xl font-black">{data.unlocked}/{data.total}</strong>
          </div>
          <div className="grid h-12 w-12 place-items-center rounded-full border-2 border-accent bg-accent/20 font-serif text-sm font-black text-accent">
            {completion}%
          </div>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surfaceAlt/12">
          <div className="h-full rounded-full bg-forest" style={{ width: `${completion}%` }} />
        </div>
      </ParchmentCard>

      <div className="mt-3 grid grid-cols-3 gap-2">
        {filters.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setFilter(item.value)}
            className={`rounded-full border px-2 py-1.5 text-[10px] font-black transition ${
              filter === item.value ? 'border-accent bg-accent text-white' : 'border-edge bg-surface text-muted'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error && <ParchmentCard className="mt-3 py-3 text-sm font-bold text-pirate-red">{error}</ParchmentCard>}

      <section className="mt-3 grid gap-2.5">
        {visibleBadges.map((badge) => <BadgeCard key={badge.id} badge={badge} onPreview={setSelectedBadge} />)}
        {loading && <ParchmentCard className="text-sm font-bold text-fg/70">A carregar medalhas...</ParchmentCard>}
        {!loading && visibleBadges.length === 0 && (
          <ParchmentCard className="text-sm font-bold text-fg/70">Nenhuma medalha nesta vista.</ParchmentCard>
        )}
      </section>

      <BadgePreviewModal badge={selectedBadge} onClose={() => setSelectedBadge(null)} />
    </main>
  );
}
